import { useState, useCallback, useRef } from 'react';
import { StoryboardExporter, StoryboardExportProgress } from '../utils/storyboardExporter';
import { IVideoClip, VideoEditConfig, IEpisode } from '@packages/global';
import { toast } from 'react-hot-toast';

export function useStoryboardExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<StoryboardExportProgress | null>(null);
  const exporterRef = useRef<StoryboardExporter | null>(null);

  const startExport = useCallback(async (
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig,
    episode: IEpisode | null,
    bgmUrl?: string
  ) => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      setExportProgress({
        stage: 'loading',
        progress: 0,
        message: '正在初始化导出...'
      });

      // 创建导出器
      const exporter = new StoryboardExporter((progress) => {
        setExportProgress(progress);
      });
      exporterRef.current = exporter;

      // 开始导出
      const zipBlob = await exporter.exportStoryboardMaterials(
        videoClips,
        editConfig,
        episode,
        bgmUrl
      );

      // 下载文件
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${episode?.title || '分镜素材'}_storyboard_materials.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('分镜素材导出成功！');
    } catch (error) {
      console.error('Storyboard export failed:', error);
      toast.error(`导出失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setIsExporting(false);
      setExportProgress(null);
      exporterRef.current = null;
    }
  }, [isExporting]);

  const cancelExport = useCallback(async () => {
    if (exporterRef.current) {
      try {
        await exporterRef.current.terminate();
        toast.success('导出已取消');
      } catch (error) {
        console.error('Cancel export failed:', error);
      }
    }
    setIsExporting(false);
    setExportProgress(null);
    exporterRef.current = null;
  }, []);

  return {
    isExporting,
    exportProgress,
    startExport,
    cancelExport
  };
}